import type {
  DropOffLocation,
  LocatorHoursDay,
  LocatorHoursOfOperation,
} from '../../../../../core/api/locator/models/locator.model';
import { formatHhmmAsDisplayTime, type PickupDisplayLocale } from './map-pickup-availability';

const DAY_ORDER: LocatorHoursDay[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Converts a locator clock string (`HH:mm` / `HH:mm:ss`) to HHmm for shared display formatting.
 */
function locatorRawToHhmm(raw: string): string | null {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(raw.trim());
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours) || hours < 0 || hours > 23) {
    return null;
  }

  return `${String(hours).padStart(2, '0')}${minutes}`;
}

/**
 * Formats locator open/close times (e.g. `07:00:00`) for customer display.
 * EN and FR both use 12-hour clocks (`7:00 AM`) via the shared display helper (OM-679).
 */
export function formatLocatorClockTime(raw: string, locale: PickupDisplayLocale = 'en'): string {
  const hhmm = locatorRawToHhmm(raw);
  if (!hhmm) {
    return raw;
  }

  return formatHhmmAsDisplayTime(hhmm, locale);
}

/**
 * Formats a single hours-of-operation row for the nearest-location card.
 */
export function formatLocatorHoursRange(
  row: LocatorHoursOfOperation,
  locale: PickupDisplayLocale = 'en',
): string | null {
  const openTime = row.open.trim();
  const closeTime = row.close.trim();
  if (!openTime || !closeTime) {
    return null;
  }

  return `${formatLocatorClockTime(openTime, locale)}-${formatLocatorClockTime(closeTime, locale)}`;
}

/**
 * Builds ordered Mon–Sun hour rows from the locator response for display.
 */
export function orderLocatorHours(hours: LocatorHoursOfOperation[]): LocatorHoursOfOperation[] {
  const byDay = new Map(hours.map((row) => [row.day, row]));
  return DAY_ORDER.map((day) => byDay.get(day)).filter((row): row is LocatorHoursOfOperation =>
    Boolean(row),
  );
}

/**
 * Formats the locator address for the nearest-location card (two-line Figma layout).
 */
export function formatLocatorAddressLines(location: DropOffLocation): {
  line1: string;
  line2: string;
} {
  const { address } = location;
  const street = [address.streetNumber, address.streetName, address.streetType]
    .filter(Boolean)
    .join(' ')
    .trim();
  const line1 = street ? `${street},` : '';
  const line2 = [address.municipalityName, address.provinceCode, address.postalCode]
    .filter(Boolean)
    .join(', ');
  return { line1, line2 };
}

/**
 * Formats radial distance for the card using ESN `convertNumber` rounding (`0.89 km`).
 * Parses the API string, rounds with `Number(n.toFixed(2))`, then appends ` km`.
 */
export function formatLocatorDistanceKm(radialDistanceInKM: string): string {
  const trimmed = radialDistanceInKM.trim();
  if (!trimmed) {
    return '';
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return '';
  }

  const rounded = Number(parsed.toFixed(2));
  return `${rounded} km`;
}

/**
 * Formats Ship From postal for the card heading (inserts a space after the FSA when missing).
 */
export function formatDisplayPostalCode(postalCode: string): string {
  const stripped = postalCode.replace(/\s+/g, '').toUpperCase();
  if (stripped.length === 6) {
    return `${stripped.slice(0, 3)} ${stripped.slice(3)}`;
  }
  return postalCode.trim();
}
